import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greyMui,
  MOBILE_BREAKPOINT,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

import {
  TIMELINE_EVENT_TICK_ACTIVE_SIZE,
  TIMELINE_EVENT_TICK_SIZE,
  TIMELINE_STATION_TICK_ACTIVE_HEIGHT,
  TIMELINE_STATION_TICK_ACTIVE_WIDTH,
  TIMELINE_STATION_TICK_HEIGHT,
  TIMELINE_STATION_TICK_WIDTH,
} from "../investigation.constants";

interface ITickStyleProps {
  $color: string;
  $isStation: boolean;
  $active?: boolean;
  $leftPercent: number;
}

const tickWidth = ({ $isStation, $active }: ITickStyleProps): number => {
  if ($isStation) {
    return $active
      ? TIMELINE_STATION_TICK_ACTIVE_WIDTH
      : TIMELINE_STATION_TICK_WIDTH;
  }

  return $active ? TIMELINE_EVENT_TICK_ACTIVE_SIZE : TIMELINE_EVENT_TICK_SIZE;
};

const tickHeight = ({ $isStation, $active }: ITickStyleProps): number => {
  if ($isStation) {
    return $active
      ? TIMELINE_STATION_TICK_ACTIVE_HEIGHT
      : TIMELINE_STATION_TICK_HEIGHT;
  }

  return $active ? TIMELINE_EVENT_TICK_ACTIVE_SIZE : TIMELINE_EVENT_TICK_SIZE;
};

const tickBoxShadow = ({
  $isStation,
  $active,
  $color,
}: ITickStyleProps): string => {
  if ($active) {
    return `0 0 0 2px ${$color}`;
  }

  return $isStation ? "none" : "0 1px 3px rgba(15, 23, 42, 0.28)";
};

const tickZIndex = ({ $active }: ITickStyleProps): number =>
  $active
    ? ScreenDepth.INVESTIGATION_TIMELINE_TICK_ACTIVE
    : ScreenDepth.INVESTIGATION_TIMELINE_TICK;

/**
 * Player transport docked at the bottom of the map column (below the map, full column width).
 * Its single-use inner rows are plain elements styled via nested `.transport-row` / `.transport-controls`
 * classes here — easier to maintain and to override per breakpoint from this one parent.
 */
export const TransportBarStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-transport",
})`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 18px 14px;
  background: ${rydeColors.white};
  border-top: 1px solid ${greyMui._200};

  .transport-row {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .transport-controls {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .transport-notice {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${greyMui._700};
  }

  .transport-notice svg {
    font-size: 18px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 8px 10px 10px;
  }
`;

/** Segmented pill grouping the speed multipliers. */
export const TransportSpeedGroupStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-speed-group",
})`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border-radius: 9px;
  background: ${greyMui._100};
`;

export const SpeedChipStyled = styled.button.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-speed-chip",
})<{ $active: boolean }>`
  border: none;
  background: ${({ $active }) => ($active ? rydeColors.white : "transparent")};
  color: ${({ $active }) => ($active ? blueMui._700 : greyMui._600)};
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  padding: 5px 9px;
  border-radius: 7px;
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? "0 1px 3px rgba(15, 23, 42, 0.18)" : "none")};
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    color: ${blueMui._700};
  }
`;

export const TransportSpacerStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-transport-spacer",
})`
  flex: 1 1 auto;
`;

export const TransportClockStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-transport-clock",
})`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.25;

  .clock-absolute {
    font-size: 15px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${blueGreyMui._900};
  }

  .clock-elapsed {
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: ${greyMui._500};
  }
`;

export const TransportStatChipStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-transport-stat",
})`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 14px;
  background: ${greyMui._100};
  font-size: 12.5px;
  color: ${blueGreyMui._800};

  svg {
    font-size: 16px;
    color: ${greyMui._600};
  }

  .stat-value {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
`;

export const TransportCalloutStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-transport-callout",
})<{ $color: string; $bg: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 300px;
  padding: 5px 12px;
  border-radius: 14px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  svg {
    font-size: 16px;
  }
`;

export const TimelineWrapperStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-timeline-wrapper",
})`
  display: flex;
  align-items: center;
`;

export const TimelineTrackStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-timeline",
})`
  position: relative;
  flex: 1 1 auto;
  height: 40px;
  /* Time always flows left->right; keep the track (and every child below) physical-LTR so it doesn't
     get mirrored in Hebrew, which would invert the drag against the pointer. @noflip stops stylis-rtl. */
  direction: ltr /* @noflip */;
  cursor: pointer;
  touch-action: none;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 8px;
    transform: translateY(-50%);
    border-radius: 4px;
    background: ${greyMui._200};
  }

  &:hover::before {
    background: ${greyMui._300};
  }
`;

export const TimelineFillStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-timeline-fill",
})`
  position: absolute;
  left: 0 /* @noflip */;
  top: 50%;
  height: 8px;
  width: 0;
  transform: translateY(-50%);
  border-radius: 4px;
  /* The fill is anchored physical-left (see @noflip above); the gradient direction may mirror in RTL,
     which is only a cosmetic shade swap on the progress bar, so it is left to flip with the locale. */
  background: linear-gradient(90deg, ${blueMui._300}, ${blueMui._600});
  pointer-events: none;
`;

export const TimelineTickStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-timeline-tick",
})<ITickStyleProps>`
  position: absolute;
  top: 50%;
  left: ${({ $leftPercent }) => $leftPercent}% /* @noflip */;
  width: ${tickWidth}px;
  height: ${tickHeight}px;
  border-radius: ${({ $isStation }) => ($isStation ? "2px" : "50%")};
  transform: translate(-50%, -50%) /* @noflip */;
  background: ${({ $color }) => $color};
  border: ${({ $isStation }) => ($isStation ? "none" : `2px solid ${rydeColors.white}`)};
  box-shadow: ${tickBoxShadow};
  z-index: ${tickZIndex};
  transition:
    width 0.15s ease,
    height 0.15s ease,
    box-shadow 0.15s ease;
  pointer-events: none;
`;

export const TimelinePlayheadStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-timeline-playhead",
})`
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 0 /* @noflip */;
  width: 2px;
  transform: translateX(-1px) /* @noflip */;
  background: ${rydeColors.reactSelectBlue};
  z-index: ${ScreenDepth.INVESTIGATION_TIMELINE_PLAYHEAD};
  pointer-events: none;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50% /* @noflip */;
    width: 16px;
    height: 16px;
    transform: translate(-50%, -50%) /* @noflip */;
    border-radius: 50%;
    background: ${rydeColors.reactSelectBlue};
    border: 3px solid ${rydeColors.white};
    box-shadow: 0 2px 5px rgba(15, 23, 42, 0.35);
  }
`;
